import { Polygon, Polyline, CircleMarker, Tooltip, Popup } from "react-leaflet";

const R = 6371000;
export function destPoint(lat, lng, brgDeg, distM) {
  const d = distM / R, b = (brgDeg * Math.PI) / 180;
  const p1 = (lat * Math.PI) / 180, l1 = (lng * Math.PI) / 180;
  const p2 = Math.asin(Math.sin(p1) * Math.cos(d) + Math.cos(p1) * Math.sin(d) * Math.cos(b));
  const l2 = l1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(p1), Math.cos(d) - Math.sin(p1) * Math.sin(p2));
  return [(p2 * 180) / Math.PI, (((l2 * 180) / Math.PI + 540) % 360) - 180];
}

// Rzut wiązki na grunt: pierścień od bliższej do dalszej krawędzi (z downtiltu), łuk = szerokość wiązki.
export function sectorPolygon(lat, lng, az, bw, outerM, innerM = 0) {
  const half = Math.min(bw, 359.9) / 2, steps = Math.max(8, Math.round(bw / 4));
  const arc = (r) => Array.from({ length: steps + 1 }, (_, i) => destPoint(lat, lng, az - half + (bw * i) / steps, r));
  const outer = arc(outerM);
  if (bw >= 359) return outer;
  return innerM > 1 ? [...outer, ...arc(innerM).reverse()] : [[lat, lng], ...outer];
}

const STATUS_COLOR = { aktywne: "#22C55E", aktywna: "#22C55E", planowane: "#38BDF8", planowana: "#38BDF8", awaria: "#EF4444", serwis: "#F59E0B", "wyłączone": "#64748B", "wyłączona": "#64748B", magazyn: "#94A3B8" };
const CAT_COLOR = { "Sector AP": "#A855F7", "Access Point": "#6366F1", CPE: "#F472B6", "Most radiowy": "#06B6D4", "Antena dookólna": "#8B5CF6" };
const SECTOR_CATS = ["Sector AP", "Access Point", "Antena sektorowa", "Antena kierunkowa", "Antena dookólna", "Most radiowy"];

function Sector({ d, showLines }) {
  const fp = d.calc?.footprint;
  const outer = fp?.outer_m || d.range_m;
  const bw = d.category === "Antena dookólna" ? 360 : (d.beamwidth_h || 60);
  const color = d.color || CAT_COLOR[d.category] || "#A855F7";
  const pts = sectorPolygon(d.lat, d.lng, d.azimuth || 0, bw, outer, fp?.inner_m || 0);
  const label = `${d.site_name ? d.site_name + " · " : ""}${d.name}${d.freq_mhz ? ` · ${d.freq_mhz} MHz` : ""}`;
  return (
    <>
      <Polygon positions={pts} pathOptions={{ color, weight: 1.5, fillColor: color, fillOpacity: d.status === "planowane" ? 0.08 : 0.18, dashArray: d.status === "planowane" ? "6 4" : null }}>
        <Tooltip sticky>{label}</Tooltip>
        <Popup>
          <b>{d.name}</b><br />{d.category} · {d.status || "—"}<br />
          Azymut {d.azimuth ?? 0}° · wiązka {bw}° · downtilt {d.downtilt ?? 0}°<br />
          Zasięg {Math.round(outer)} m{fp?.main_m ? ` · oś wiązki na gruncie ${fp.main_m} m` : ""}<br />
          {d.calc?.eirp_dbm != null && <>EIRP {d.calc.eirp_dbm} dBm<br /></>}
          {(d.calc?.regulatory || []).filter((r) => r.level === "error").map((r, i) => <span key={i} style={{ color: "#EF4444" }}>{r.msg}<br /></span>)}
        </Popup>
      </Polygon>
      {showLines && bw < 359 && [0, -bw / 2, bw / 2].map((o) => (
        <Polyline key={o} positions={[[d.lat, d.lng], destPoint(d.lat, d.lng, (d.azimuth || 0) + o, outer)]}
          pathOptions={{ color, weight: o === 0 ? 2 : 1, dashArray: o === 0 ? null : "4 4", opacity: 0.9 }} />
      ))}
    </>
  );
}

export default function WifiMapLayer({ wifi, showLines = true }) {
  if (!wifi) return null;
  const { sites = [], devices = [], links = [] } = wifi;
  return (
    <>
      {devices.filter((d) => SECTOR_CATS.includes(d.category) && d.lat != null && (d.range_m || d.calc?.footprint)).map((d) => <Sector key={`s-${d.id}`} d={d} showLines={showLines} />)}
      {links.filter((l) => l.calc?.distance_m != null).map((l) => (
        <Polyline key={`l-${l.id}`} positions={[[l.calc.a.lat, l.calc.a.lng], [l.calc.b.lat, l.calc.b.lng]]}
          pathOptions={{ color: STATUS_COLOR[l.status] || "#06B6D4", weight: 3, dashArray: l.status === "planowane" ? "8 6" : null }}>
          <Tooltip sticky>{l.name} · {(l.calc.distance_m / 1000).toFixed(2)} km · {l.calc.azimuth_ab}°/{l.calc.azimuth_ba}°</Tooltip>
        </Polyline>
      ))}
      {devices.filter((d) => d.category === "CPE" && d.lat != null && !d.pos_from_site).map((d) => (
        <CircleMarker key={`c-${d.id}`} center={[d.lat, d.lng]} radius={5} pathOptions={{ color: "#fff", weight: 1, fillColor: STATUS_COLOR[d.status] || CAT_COLOR.CPE, fillOpacity: 1 }}>
          <Tooltip>{d.name} (CPE)</Tooltip>
        </CircleMarker>
      ))}
      {sites.filter((s) => s.lat != null).map((s) => (
        <CircleMarker key={`b-${s.id}`} center={[s.lat, s.lng]} radius={8} pathOptions={{ color: "#0F172A", weight: 2, fillColor: STATUS_COLOR[s.status] || "#A855F7", fillOpacity: 1 }}>
          <Tooltip direction="top">{s.name}</Tooltip>
          <Popup><b>{s.name}</b>{s.code ? ` (${s.code})` : ""}<br />{s.status} · maszt {s.mast_height_m ?? "—"} m<br />
            Nadajniki: {s.counts?.transmitters ?? 0} · sektory: {s.counts?.sectors ?? 0} · CPE: {s.counts?.cpe ?? 0} · PTP: {s.counts?.ptp ?? 0}</Popup>
        </CircleMarker>
      ))}
    </>
  );
}
